import React, { useEffect, useMemo, useState } from 'react';
import { Link } from "react-router-dom";

import BasicCRUD from './../core/basic-crud';
import { socketUseEffect } from "../../socket";
import BadgeStatusView from "./sub-pages/elements/BadgeStatusView";
import { useAuth } from "../../AuthContext";
import ProgressBar from "../page-elements/progress-bar";
import FadeAlert from "../core/fade-alert";
import resolveUserRole from "../utils/resolveUserRole";
import RangeSlider from "../core/range-slider";

const ToggleSwitch = ({ label, checked = false, disabled = false, onChange }) => (
    <div className="p-3 border rounded-3 bg-white shadow-sm d-flex align-items-center justify-content-between">
        {label && <div className="fw-semibold text-dark me-3 mb-0">{label}</div>}
        <div className="form-check form-switch mb-0">
            <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id={`switch-${label}`}
                checked={!!checked}
                disabled={disabled}
                onChange={(e) => onChange?.(e.target.checked, e)}
            />
        </div>
    </div>
);

const NotificationBlock = ({
    labelToggle,
    runsLabel,
    thresholdLabel,
    toggleKey,
    runsKey,
    thresholdKey,
    defaultRuns = 2,
    defaultThreshold = 80,
    data,
    field,
    fieldsData,
    fieldsChange,
}) => {
    console.log("slider control")
    const toggleVal = String(
        fieldsData?.[toggleKey] ??
        data[toggleKey] ??
        "1",
    );

    const dataSafe = fieldsData || {};

    //console.log("runs:: ", data, dataSafe)
    const runsVal = Number(
        data[runsKey] ??
        dataSafe[runsKey] ??
        field?.default_value ??
        defaultRuns,
    ) || defaultRuns;

    //const runsVal = Number(
    //    data[runsKey] ??
    //    fieldsData?.[runsKey] ??
    //    defaultRuns,
    //) || defaultRuns;

    const thresholdVal = Number(
        data[thresholdKey] ??
        fieldsData?.[thresholdKey] ??
        defaultThreshold,
    ) || defaultThreshold;

    const enabled = toggleVal === "1";

    return (
        <div className="card h-100 border-0 shadow-sm bg-white p-3 rounded-3" style={{ marginBottom: 0 }}>
            <div className="row g-3 align-items-center">
                <div className="col-lg-4">
                    <ToggleSwitch
                        label={labelToggle}
                        checked={enabled}
                        onChange={(next) => fieldsChange({ target: { value: next ? "1" : "0" } }, toggleKey)}
                    />
                </div>
                <div className="col-lg-4">
                    <RangeSlider
                        label={runsLabel}
                        value={runsVal}
                        disabled={!enabled}
                        min={1}
                        max={10}
                        step={1}
                        leftLabel="1"
                        rightLabel="10"
                        onChange={(next) => fieldsChange({ target: { value: next } }, runsKey)}
                    />
                </div>
                <div className="col-lg-4">
                    <RangeSlider
                        label={thresholdLabel}
                        value={thresholdVal}
                        disabled={!enabled}
                        min={5}
                        max={100}
                        step={1}
                        leftLabel="5%"
                        rightLabel="100%"
                        formatValue={(v) => `${v}%`}
                        onChange={(next) => fieldsChange({ target: { value: next } }, thresholdKey)}
                    />
                </div>
            </div>
        </div>
    );
};

// ---------------- i18n ----------------
const dict = {
    en: {
        // titles
        title: "Servers",
        subTitle: "add / edit servers",
        homeURL: "/dashboard",
        addServer: "Add Server",
        editServer: "Edit Server",

        // table columns
        colStatus: "Status",
        colServerName: "Server Name",
        colCreated: "Created",
        colContactGroup: "Contact Group",

        // inline UI
        serverDetails: "Server Details",
        unknownServer: "Unknown Server",
        createdDate: "Created Date:",
        CPU: "CPU",
        RAM: "RAM",
        HDD: "HDD",

        // selects & options
        choose: " -- choose -- ",
        yes: "yes",
        no: "no",
        contactGroup: "Contact Group",
        serverName: "Server Name",

        useCpu: "Use CPU Notifications",
        cpuThreshold: "CPU Notification Threshold (%)",
        cpuRuns: "CPU Notification Runs",

        useHdd: "Use HDD Notifications",
        hddThreshold: "HDD Notification Threshold (%)",
        hddRuns: "HDD Notification Runs",

        useRam: "Use RAM Notifications",
        ramThreshold: "RAM Notification Threshold (%)",
        ramRuns: "RAM Notification Runs",

        // actions
        edit: "Edit",
        delete: "Delete",

        // disabled tooltips
        noResources: "You don't have any available resources. Please change your pricing plan to get more items.",
        planExpired: "Your pricing plan has expired. Please renew or upgrade your plan to continue accessing this feature.",

        // delete dialog
        delAlert: "Deleting a server cannot be undone.",
        delText: "To confirm deletion, enter the name of the server in the next input field",
        delTitle: 'Delete Server: "{name}"',

        // toast
        createdOkA: "Server",
        createdOkB: "has been successfully created.",
        updatedOkB: "has been successfully updated.",
        deletedOkB: "has been successfully deleted.",
        memberViewOnly: "Members can only view servers.",
    },
    ru: {
        title: "Серверы",
        subTitle: "добавление / редактирование серверов",
        homeURL: "/dashboard",
        addServer: "Добавить сервер",
        editServer: "Редактировать сервер",

        colStatus: "Статус",
        colServerName: "Имя сервера",
        colCreated: "Создан",
        colContactGroup: "Группа контактов",

        serverDetails: "Детали сервера",
        unknownServer: "Неизвестный сервер",
        createdDate: "Дата создания:",
        CPU: "CPU",
        RAM: "RAM",
        HDD: "HDD",

        choose: " -- выберите -- ",
        yes: "да",
        no: "нет",
        contactGroup: "Группа контактов",
        serverName: "Имя сервера",

        useCpu: "Уведомления по CPU",
        cpuThreshold: "Порог уведомлений CPU (%)",
        cpuRuns: "Количество срабатываний CPU",

        useHdd: "Уведомления по HDD",
        hddThreshold: "Порог уведомлений HDD (%)",
        hddRuns: "Количество срабатываний HDD",

        useRam: "Уведомления по RAM",
        ramThreshold: "Порог уведомлений RAM (%)",
        ramRuns: "Количество срабатываний RAM",

        edit: "Редактировать",
        delete: "Удалить",

        noResources: "У вас нет доступных ресурсов. Пожалуйста, измените тарифный план, чтобы получить больше элементов.",
        planExpired: "Срок действия вашего тарифного плана истёк. Обновите или продлите план, чтобы продолжить пользоваться функцией.",

        delAlert: "Удаление сервера необратимо.",
        delText: "Чтобы подтвердить удаление, введите имя сервера в следующем поле ввода",
        delTitle: 'Удалить сервер: «{name}»',

        createdOkA: "Сервер",
        createdOkB: "успешно создан.",
        updatedOkB: "успешно обновлён.",
        deletedOkB: "успешно удалён.",
        memberViewOnly: "У участников нет прав управлять серверами.",
    },
    ua: {
        title: "Сервери",
        subTitle: "додавання / редагування серверів",
        homeURL: "/dashboard",
        addServer: "Додати сервер",
        editServer: "Редагувати сервер",

        colStatus: "Статус",
        colServerName: "Назва сервера",
        colCreated: "Створено",
        colContactGroup: "Група контактів",

        serverDetails: "Деталі сервера",
        unknownServer: "Невідомий сервер",
        createdDate: "Дата створення:",
        CPU: "CPU",
        RAM: "RAM",
        HDD: "HDD",

        choose: " -- оберіть -- ",
        yes: "так",
        no: "ні",
        contactGroup: "Група контактів",
        serverName: "Назва сервера",

        useCpu: "Сповіщення CPU",
        cpuThreshold: "Поріг сповіщень CPU (%)",
        cpuRuns: "Кількість спрацювань CPU",

        useHdd: "Сповіщення HDD",
        hddThreshold: "Поріг сповіщень HDD (%)",
        hddRuns: "Кількість спрацювань HDD",

        useRam: "Сповіщення RAM",
        ramThreshold: "Поріг сповіщень RAM (%)",
        ramRuns: "Кількість спрацювань RAM",

        edit: "Редагувати",
        delete: "Видалити",

        noResources: "У вас немає доступних ресурсів. Будь ласка, змініть тарифний план, щоб отримати більше елементів.",
        planExpired: "Строк дії вашого тарифного плану минув. Поновіть або оновіть план, щоб продовжити користування функцією.",

        delAlert: "Видалення сервера неможливо скасувати.",
        delText: "Щоб підтвердити видалення, введіть назву сервера у наступному полі вводу",
        delTitle: 'Видалити сервер: «{name}»',

        createdOkA: "Сервер",
        createdOkB: "успішно створено.",
        updatedOkB: "успішно оновлено.",
        deletedOkB: "успішно видалено.",
        memberViewOnly: "У учасників немає прав керувати серверами.",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

// ---------------- component ----------------
const Servers = ({ lang: langProp, ...props }) => {
    const lang = (langProp || getInitialLang());
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const object = "servers";
    const [response, setResponse] = useState({});
    const [fieldsData, setFieldsData] = useState({});
    const [totalData, setTotalData] = useState([]);
    const [contacts, setContacts] = useState([{ id: 0, name: t.choose }]);

    const { user } = useAuth();
    const currentUserRole = useMemo(() => resolveUserRole(user), [user]);
    const canManageServers = currentUserRole !== "member";
    const listeners = [
        { name: "contacts", fn: "items-list", args: { user_id: user.user_id, site_id: props.site_id }, saveDataFN: setContacts },
        { name: "dashboard", fn: "reports-total", args: {}, saveDataFN: setTotalData },
    ];
    useEffect(() => socketUseEffect(listeners, user), [response?.status, response?.created, response?.updated, response?.delete]);

    // Prefill при одиночном редактировании
    const hasInitial = Object.keys(fieldsData || {}).length > 0;
    useEffect(() => {
        if (props.singleArgs?.edit && props.singleArgs.details && !hasInitial) {
            setFieldsData(props.singleArgs.details);
        }
    }, [props.singleArgs?.edit, props.singleArgs?.details, hasInitial]);

    const formatStatus = (data) => <BadgeStatusView status={data['status']} lang={lang} />;

    const formatFieldDate = (data, key) => {
        const serverName = data['name'] || t.unknownServer;
        const date = new Date(data[key]);
        if (isNaN(date)) return 'Invalid Date';

        const options = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };

        return (
            <>
                <Link to={`/server/${data.id}/dashboard`} className="sidebar-link" data-text={t.serverDetails}>
                    <span style={{ fontWeight: 'bold', fontSize: 22 }}>{serverName}</span>
                </Link><br />
                <span style={{ fontWeight: 'bold', fontSize: 12, width: 80 }}>{t.createdDate}</span>{" "}
                <span style={{ fontSize: 12, color: 'rgb(108, 117, 125)' }}>
          {date.toLocaleDateString('en-GB', options).replace(',', '')}
        </span>
            </>
        );
    };

    const formatCreatedDateString = (data, key) => {
        const date = new Date(data[key]);
        if (isNaN(date)) return '-';

        const options = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
        return date.toLocaleDateString('en-GB', options).replace(',', '');
    };

    const formatBytes = (bytes) => {
        if (bytes >= 1024 ** 3) return `${(bytes / (1024 ** 3)).toFixed(2)} GB`;
        if (bytes >= 1024 ** 2) return `${(bytes / (1024 ** 2)).toFixed(2)} MB`;
        if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
        return `${bytes} B`;
    };

    const formatServerDetails = (data) => {
        const cpuValue = parseFloat(data['cpu']) || 0;
        const formattedCPU = cpuValue < 0.01 ? "0.01%" : `${cpuValue.toFixed(2)}%`;
        const cpuThreshold = data.cpu_notifies_ptc || 100;
        const cpuHighlight = data.use_cpu_notifies === 1 && cpuValue > cpuThreshold;
        const cpuCritical = data.use_cpu_notifies === 1 && cpuValue > cpuThreshold + (100 - cpuThreshold) / 2;

        const usedRAM = (data.ram || 0) * 1024;
        const totalRAM = (data.ram_all || 0) * 1024;
        const ramPercentage = (usedRAM / totalRAM) * 100;
        const formattedRAM = `${formatBytes(usedRAM)} / ${formatBytes(totalRAM)}`;
        const ramThreshold = data.ram_notifies_ptc || 100;
        const ramHighlight = data.use_ram_notifies === 1 && ramPercentage > ramThreshold;
        const ramCritical = data.use_ram_notifies === 1 && ramPercentage > ramThreshold + (100 - ramThreshold) / 2;

        const usedHDD = (data.hdd || 0) * 1024;
        const totalHDD = (data.hdd_all || 0) * 1024;
        const hddPercentage = (usedHDD / totalHDD) * 100;
        const formattedHDD = `${formatBytes(usedHDD)} / ${formatBytes(totalHDD)}`;
        const hddThreshold = data.hdd_notifies_ptc || 100;
        const hddHighlight = data.use_hdd_notifies === 1 && hddPercentage > hddThreshold;
        const hddCritical = data.use_hdd_notifies === 1 && hddPercentage > hddThreshold + (100 - hddThreshold) / 2;

        const valueStyle = (highlight, critical) => ({
            fontSize: 12,
            color: critical ? '#f20000' : highlight ? '#ff9a44' : '#333',
            fontWeight: (critical || highlight) ? 'bold' : 'normal',
        });

        const StatRow = ({ label, value, highlight, critical, pct }) => (
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-2 flex-grow-1">
                <div className="d-flex align-items-center gap-2 flex-grow-1">
                    <span className="badge bg-light text-dark fw-semibold" style={{ minWidth: 48 }}>{label}</span>
                    <div className="flex-grow-1 position-relative rounded-pill overflow-hidden" style={{ height: 10, background: "#eef1f4" }}>
                        <div
                            className="h-100"
                            style={{
                                width: `${Math.min(Math.max(pct, 0), 100)}%`,
                                background: critical ? "#f20000" : highlight ? "#ff9a44" : "#4CAF50",
                                transition: "width 0.2s ease",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: pct >= 15 ? "#fff" : "transparent",
                                fontSize: 10,
                                fontWeight: 700,
                            }}
                        >
                            {`${Math.min(Math.max(pct, 0), 100).toFixed(0)}%`}
                        </div>
                    </div>
                </div>
                <span style={valueStyle(highlight, critical)} className="text-nowrap">
                    {value}
                </span>
            </div>
        );

        return (
            <div className="w-100">
                <div className="border rounded-3 p-3 bg-body-tertiary d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <StatRow label={t.CPU} value={formattedCPU} highlight={cpuHighlight} critical={cpuCritical} pct={cpuValue} />
                    <StatRow label={t.RAM} value={formattedRAM} highlight={ramHighlight} critical={ramCritical} pct={ramPercentage || 0} />
                    <StatRow label={t.HDD} value={formattedHDD} highlight={hddHighlight} critical={hddCritical} pct={hddPercentage || 0} />
                </div>
            </div>
        );
    };

    const columns = [
        { label: t.colStatus, key: "status", format: formatStatus },
        { label: t.colServerName, key: "created_date", format: formatFieldDate },
        { label: t.colCreated, key: "created_date", format: formatServerDetails, hide: true },
        { label: t.colContactGroup, key: "contact_group_name" },
    ];

    const showField = (data, field) => {
        const key = field.key;
        const updated = { ...field };
        if ((key === "cpu_notifies_ptc" || key === "cpu_notifies_runs") && String(data['use_cpu_notifies']) === "0") updated.readonly = true;
        if ((key === "hdd_notifies_ptc" || key === "hdd_notifies_runs") && String(data['use_hdd_notifies']) === "0") updated.readonly = true;
        if ((key === "ram_notifies_ptc" || key === "ram_notifies_runs") && String(data['use_ram_notifies']) === "0") updated.readonly = true;
        return updated;
    };

    const fields = [
        {
            type: "tab",
            name: "Main",
            showField,
            fields: [
                [
                    { label: t.serverName, key: "name", default_value: "", showLabel: "top" },
                    { label: t.contactGroup, key: "contact_group_id", default_value: "", type: "selectbox", data: contacts, showLabel: "top" }
                ],
                // скрытые поля, чтобы значения подхватывались в кастомных слайдерах/переключателях
                [
                    { key: "cpu_notifies_runs", type: "text", showLabel: false, hidden: true, default_value: "2" },
                    { key: "cpu_notifies_ptc", type: "text", showLabel: false, hidden: true, default_value: "80" },
                    { key: "use_cpu_notifies", type: "text", showLabel: false, hidden: true, default_value: "1" },
                    { key: "hdd_notifies_runs", type: "text", showLabel: false, hidden: true, default_value: "2" },
                    { key: "hdd_notifies_ptc", type: "text", showLabel: false, hidden: true, default_value: "80" },
                    { key: "use_hdd_notifies", type: "text", showLabel: false, hidden: true, default_value: "1" },
                    { key: "ram_notifies_runs", type: "text", showLabel: false, hidden: true, default_value: "2" },
                    { key: "ram_notifies_ptc", type: "text", showLabel: false, hidden: true, default_value: "80" },
                    { key: "use_ram_notifies", type: "text", showLabel: false, hidden: true, default_value: "1" },
                ],
                [
                    {
                        label: t.useCpu,
                        key: "use_cpu_notifies",
                        type: "custom",
                        showLabel: false,
                        fn: ({ fieldsChange, fieldsData, field, data }) => (
                            <NotificationBlock
                                labelToggle={t.useCpu}
                                runsLabel={t.cpuRuns}
                                runsKey="cpu_notifies_runs"
                                thresholdLabel={t.cpuThreshold}
                                thresholdKey="cpu_notifies_ptc"
                                toggleKey="use_cpu_notifies"
                                data={data}
                                field={field}
                                fieldsData={fieldsData}
                                fieldsChange={fieldsChange}
                            />
                        ),
                    },
                ],
                [
                    {
                        label: t.useHdd,
                        key: "use_hdd_notifies",
                        type: "custom",
                        showLabel: false,
                        fn: ({ fieldsChange, data, fieldsData }) => (
                            <NotificationBlock
                                labelToggle={t.useHdd}
                                runsLabel={t.hddRuns}
                                runsKey="hdd_notifies_runs"
                                thresholdLabel={t.hddThreshold}
                                thresholdKey="hdd_notifies_ptc"
                                toggleKey="use_hdd_notifies"
                                data={data}
                                fieldsData={fieldsData}
                                fieldsChange={fieldsChange}
                            />
                        ),
                    },
                ],
                [
                    {
                        label: t.useRam,
                        key: "use_ram_notifies",
                        type: "custom",
                        showLabel: false,
                        fn: ({ fieldsChange, data, fieldsData }) => (
                            <NotificationBlock
                                labelToggle={t.useRam}
                                runsLabel={t.ramRuns}
                                runsKey="ram_notifies_runs"
                                thresholdLabel={t.ramThreshold}
                                thresholdKey="ram_notifies_ptc"
                                toggleKey="use_ram_notifies"
                                data={data}
                                fieldsData={fieldsData}
                                fieldsChange={fieldsChange}
                            />
                        ),
                    },
                ],
            ],
        },
    ];

    const actions = canManageServers ? [
        { name: "edit", label: t.edit, controller: "crud", icon: "ti ti-edit" },
        { name: "delete", label: t.delete, controller: "crud", icon: "ti ti-trash" },
    ] : [];

    const renderServerCard = (item, helpers) => {
        const contactGroup = item.contact_group_name || t.unknownServer;
        const metricsBlock = formatServerDetails(item);
        const actionsList = helpers.actions || [];
        const createdDateText = formatCreatedDateString(item, "created_date");
        const serverName = item.name || t.unknownServer;

        return (
            <Link to={`/server/${item.id}/dashboard`} className="text-decoration-none text-dark">
                <div className="card h-100 shadow-sm border">
                    <div className="card-body d-flex flex-column">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            {formatStatus(item)}
                            <span className="badge bg-primary-subtle text-primary">
                                {contactGroup}
                            </span>
                        </div>

                        <div className="mb-3">
                            <div style={{ fontWeight: 'bold', fontSize: 22, marginBottom: 4 }}>{serverName}</div>
                            <div>
                                <span style={{ fontWeight: 'bold', fontSize: 12, width: 80 }}>{t.createdDate}</span>{" "}
                                <span style={{ fontSize: 12, color: 'rgb(108, 117, 125)' }}>{createdDateText}</span>
                            </div>
                        </div>

                        <div className="mb-4">
                            {metricsBlock}
                        </div>

                        {actionsList.length > 0 && (
                            <div className="mt-auto d-flex gap-2 justify-content-end flex-wrap">
                                {actionsList.map((action) => (
                                    <button
                                        key={action.name}
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        title={action.label}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            helpers.tableAction?.(action.name, item);
                                        }}
                                    >
                                        <i className={action.icon || "ti ti-dots"}></i>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </Link>
        );
    };

    const disabled = (() => {
        if (!canManageServers) {
            return { add: { disable: true, title: t.memberViewOnly } };
        }
        const expired = user?.expired ?? 1;
        if (expired) {
            return { add: { disable: true, title: t.planExpired } };
        }
        const noQuota = (totalData.servers - totalData.used_servers) < 1;
        return { add: { disable: noQuota, title: t.noResources } };
    })();

    const singleMode = props.singleArgs?.edit || props.singleArgs?.delete;
    // BasicCRUD сам держит данные таблицы, поэтому здесь используем данные из singleArgs для одиночного режима, иначе оставляем undefined
    const dataForCards = singleMode && props.singleArgs?.details ? [props.singleArgs.details] : undefined;

    return (
        <>
            <BasicCRUD
                singleArgs={props?.singleArgs ?? false}
                title={t.title}
                subTitle={t.subTitle}
                homeURL={t.homeURL}
                addTitle={t.addServer}
                disable={disabled}
                editTitle={t.editServer}
                object={object}
                columns={columns}
                fields={fields}
                actions={actions}
                fieldsData={fieldsData}
                setFieldsData={setFieldsData}
                response={response}
                setResponse={setResponse}
                ownHandler={true}
                singleFormWrapperClass="card border-0 shadow-sm rounded-4 bg-light-subtle p-4"
                singleFormInnerClass="row g-4"
                singleFormButtonsClass="d-flex justify-content-end"
                singleFormButtonStyle={{ marginTop: 0, marginBottom: 0, minWidth: "160px" }}
                deleteConfirmationDialog={{
                    alert: t.delAlert,
                    text: t.delText,
                    title: t.delTitle,
                    column: 'name',
                }}
                viewModes={["table", "blocks"]}
                renderItem={renderServerCard}
                cardLayout={{ columnClass: "col-12 col-lg-6 col-xxl-4" }}
                data={dataForCards}
            >
                {!props.singleArgs && <ProgressBar lang={lang} used={totalData.used_servers} allowed={totalData.servers} />}

                {props.singleArgs?.delete && (
                    <div className="alert bg-danger-subtle border-0 text-danger-emphasis rounded-4 mb-3">
                        <div className="fw-semibold mb-1">
                            {t.delTitle.replace("{name}", props.singleArgs?.details?.name || t.unknownServer)}
                        </div>
                        <div className="small mb-1">
                            Deleting this server cannot be undone. Please confirm the server name to proceed.
                        </div>
                        <div className="small text-muted mb-0">
                            Host: {props.singleArgs?.details?.host || props.singleArgs?.details?.name || "—"}
                        </div>
                    </div>
                )}

                {response.status && response.status === 1 ? (
                    <FadeAlert response={response} resetResponse={() => setResponse([])}>
                        {response.created === 1 && (<><strong>{t.createdOkA}</strong> {t.createdOkB}</>)}
                        {response.updated === 1 && (<><strong>{t.createdOkA}</strong> {t.updatedOkB}</>)}
                        {response.delete === 1 && (<><strong>{t.createdOkA}</strong> {t.deletedOkB}</>)}
                    </FadeAlert>
                ) : null}
            </BasicCRUD>
        </>
    );
};

export default Servers;
